// MLC hours-of-rest engine (the legal core - review carefully), shared by the Hours of Rest page, the Monthly Review
// and the AI so all three judge a record the same way. Pure: no React, no browser, date strings only.
// Operates on flat arrays of 48 half-hour blocks per day. 1 = work, 0 = rest.
import { addDays } from './shipTime.js'


// Given a blocks array (rest=0), return rest periods as [{startIdx,len}] (contiguous rest runs)
export function restPeriods(blocks) {
  const periods = []
  let run = 0, start = -1
  for (let i = 0; i < blocks.length; i++) {
    if (blocks[i] === 0) {
      if (run === 0) start = i
      run++
    } else {
      if (run > 0) periods.push({ start, len: run })
      run = 0
    }
  }
  if (run > 0) periods.push({ start, len: run })
  return periods
}

// Evaluate the 24h-window rules on a single day's 48 blocks.
// (Rolling cross-day windows handled separately in monthCompliance.)
export function evaluateDay(blocks, ctx) {
  // Structure (>=6h continuous, <=2 periods, <=14h between) must see rest that
  // crosses midnight as ONE period. With ctx = {timeline, dayStart}, extend any
  // rest run touching the day's edges into the neighbouring days.
  let allPeriods = restPeriods(blocks)
  if (ctx && ctx.timeline) {
    const { timeline, dayStart, hasPrev, hasNext } = ctx
    // Extend ONLY into neighbouring days that actually contain data.
    // Missing days are displayed as rest but must never fabricate rest here.
    allPeriods = allPeriods.map(p => {
      let start = p.start, len = p.len
      if (p.start === 0 && hasPrev) {                        // extend backwards into real data
        let k = dayStart - 1
        while (k >= 0 && timeline[k] === 0) { start--; len++; k-- }
      }
      if (p.start + p.len === blocks.length && hasNext) {    // extend forwards into real data
        let k = dayStart + blocks.length
        while (k < timeline.length && timeline[k] === 0) { len++; k++ }
      }
      return { start, len }
    })
    // merging note: if the WHOLE day is rest, backwards+forwards extension is one run already
  }
  const totalRestBlocks = blocks.reduce((a, b) => a + (b === 0 ? 1 : 0), 0)
  const totalRestHours = totalRestBlocks * 0.5
  const longestRest = allPeriods.reduce((m, p) => Math.max(m, p.len), 0) * 0.5

  // Qualifying rest periods: >= 1 hour (>= 2 half-hour blocks). MLC excludes short breaks.
  const qualifying = allPeriods.filter(p => p.len >= 2).map(p => p.len * 0.5) // hours, descending later
  qualifying.sort((a, b) => b - a)

  // Rule: >=6h continuous rest
  const rule6h = longestRest >= 6
  // Rule: >=10h total rest in 24h
  const rule10h = totalRestHours >= 10

  // CORE MLC TEST: can we satisfy 10h using AT MOST 2 qualifying periods, one of which is >=6h?
  // Pick the single longest period IF it is >=6h, then add the next longest period.
  // If that pair (<=2 periods) totals >=10h -> compliant. Extra periods are bonus, never penalised.
  let canMeetInTwo = false
  if (qualifying.length >= 1 && qualifying[0] >= 6) {
    const best = qualifying[0]
    const second = qualifying.length >= 2 ? qualifying[1] : 0
    if (best >= 10 || (best + second) >= 10) canMeetInTwo = true
  }

  // Interval between consecutive qualifying rest periods <= 14h (on the actual timeline)
  const qp = allPeriods.filter(p => p.len >= 2)
  let rule14h = true
  for (let i = 1; i < qp.length; i++) {
    const prevEnd = qp[i-1].start + qp[i-1].len
    const gap = (qp[i].start - prevEnd) * 0.5
    if (gap > 14) rule14h = false
  }

  // A day is compliant if: there is a >=6h continuous period AND the 10h can be met in <=2 periods.
  // (rule10h is implied by canMeetInTwo, but kept for display.)
  const compliant = rule6h && canMeetInTwo && rule14h

  return {
    totalRestHours,
    longestRest,
    rule6h,
    rule10h,
    rule2periods: canMeetInTwo,   // reuse existing flag name in the UI: "10h met in <=2 periods"
    rule14h,
    violation: !compliant,
  }
}

// Build a continuous block timeline for a date using prev-day tail + day + next-day head.
// Returns { timeline: [...144 blocks], dayStart: 48 } where index 48..95 is the target day.
export function buildTimeline(daysMap, dateStr) {
  // date strings only (UTC arithmetic in shipTime.addDays): same result in the browser and on the server
  const getBlocks = (k) => {
    const dd = daysMap[k]
    if (!dd) return null
    if (dd.status && dd.status !== 'Onboard') return new Array(48).fill(0) // leave = rest
    return dd.blocks
  }
  const pb = getBlocks(addDays(dateStr, -1))
  const cb = getBlocks(dateStr) || new Array(48).fill(0)
  const nb = getBlocks(addDays(dateStr, 1))
  const timeline = [
    ...(pb || new Array(48).fill(0)),
    ...cb,
    ...(nb || new Array(48).fill(0)),
  ]
  return { timeline, dayStart: 48, hasPrev: pb !== null, hasNext: nb !== null }
}

// Test a single 48-block (24h) window: >=10h of qualifying rest (periods >=1h).
// NOTE: only the 10h TOTAL is a rolling any-24h rule. The structural rules
// (<=2 periods, one >=6h, <=14h between) apply to the day's rest pattern and
// are checked in evaluateDay - applying them to every sliding window would
// condemn any split-rest roster the moment a window bisects the long block,
// which contradicts evaluateDay, mlcRoster.js, and industry practice.
function windowOk(window) {
  const periods = restPeriods(window)
  const qualifyingRest = periods.filter(p => p.len >= 2).reduce((a, p) => a + p.len, 0) * 0.5
  return qualifyingRest >= 10
}

// Rolling 24h: slide a 48-block window across the target day. The day passes only if
// EVERY 24h window that starts within the target day is compliant.
export function evaluateRolling24(daysMap, dateStr) {
  const { timeline, dayStart } = buildTimeline(daysMap, dateStr)
  let allOk = true
  let worstWindowStartHalfHour = null
  // window start positions: every half-hour block within the target day
  for (let offset = 0; offset < 48; offset++) {
    const startIdx = dayStart + offset
    const window = timeline.slice(startIdx, startIdx + 48)
    if (window.length < 48) break
    if (!windowOk(window)) {
      allOk = false
      if (worstWindowStartHalfHour === null) worstWindowStartHalfHour = offset
    }
  }
  return { ok: allOk, firstFailOffset: worstWindowStartHalfHour }
}


// Rolling 7-day rest total ending on dateStr (that day + prior 6).
// Non-onboard (leave) days count as full rest (24h). Days outside available data are skipped (partial window).
export function rollingSevenDayRest(daysMap, dateStr) {
  let totalRest = 0
  let counted = 0
  for (let i = 0; i < 7; i++) {
    const key = addDays(dateStr, -i)
    const dd = daysMap[key]
    if (!dd) continue
    counted++
    if (dd.status && dd.status !== 'Onboard') {
      totalRest += 24
    } else {
      const restBlocks = dd.blocks.reduce((a,b) => a + (b === 0 ? 1 : 0), 0)
      totalRest += restBlocks * 0.5
    }
  }
  return { totalRest, daysCounted: counted, complete: counted === 7 }
}


// days of one hours_rest row: the column is a JSON string (or already an object)
export function parseDays(v) {
  if (!v) return {}
  if (typeof v === 'object') return v
  try { return JSON.parse(v) || {} } catch (e) { return {} }
}

// Non-conformities of one month (YYYY-MM) from hours_rest rows (snake_case). Rows of the neighbouring months may be
// passed too: they are merged per crew member for the windows at the month edges, but only days of `month` are judged.
// Rules: '6h' (6h continuous), '10h' (10h total), '2 periods' (10h in at most 2 periods, one >= 6h), '14h' (<= 14h between
// rest periods), '24h' (any rolling 24h >= 10h), '77h/7d' (>= 77h in 7 days, only judged when all 7 days are recorded).
export function monthNonConformities(rows, month) {
  const byCrew = {}
  for (const r of rows || []) {
    const who = r.crew_member || r.crew_id || '?'
    const days = parseDays(r.days)
    byCrew[who] = byCrew[who] || {}
    for (const [k, v] of Object.entries(days)) if (!byCrew[who][k] || String(r.month) === month) byCrew[who][k] = v
  }
  const items = []
  for (const [who, days] of Object.entries(byCrew)) {
    for (const date of Object.keys(days).filter(d => d.slice(0, 7) === month).sort()) {
      const dd = days[date]
      if (!dd || !Array.isArray(dd.blocks)) continue
      const rules = []
      if (!dd.status || dd.status === 'Onboard') {
        const c = evaluateDay(dd.blocks, buildTimeline(days, date))
        if (!c.rule6h) rules.push('6h')
        if (!c.rule10h) rules.push('10h')
        if (!c.rule2periods) rules.push('2 periods')
        if (!c.rule14h) rules.push('14h')
        if (!evaluateRolling24(days, date).ok) rules.push('24h')
      }
      const s7 = rollingSevenDayRest(days, date)
      if (s7.complete && s7.totalRest < 77) rules.push('77h/7d')
      if (rules.length) items.push({ crew_member: who, date, rules })
    }
  }
  return { crew: new Set(items.map(i => i.crew_member)).size, days: items.length, items }
}
